import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Loader2 } from "lucide-react";
import { useCancelSession } from "@/lib/api/sessions.ts";
import { useToast } from "@/components/ui/use-toast.ts";

interface CancelSessionConfirmationModalProps {
  menteeId: string;
  sessionId: string;
  open: boolean;
  onClose: () => void;
}

export function CancelSessionConfirmationModal({
  sessionId,
  open,
  menteeId,
  onClose,
}: CancelSessionConfirmationModalProps) {
  const { toast } = useToast();
  const { mutateAsync: cancelSession, isPending } = useCancelSession(menteeId);

  const handleCancelSession = async () => {
    try {
      await cancelSession({
        session_id: sessionId,
      });

      toast({
        title: "Session cancelled",
        description: "The session has been successfully cancelled.",
      });
    } catch (error) {
      console.log(error);

      toast({
        title: "Error",
        description: "An error occurred while cancelling the session.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        if (!newOpen) {
          onClose();
        }
      }}
    >
      <DialogContent className="w-full max-w-lg p-0" hideCloseButton>
        <div className="p-4">
          <DialogHeader>
            <DialogTitle className="mb-1 text-2xl font-semibold">
              Are you sure you want to cancel the session?
            </DialogTitle>
            <DialogDescription className="text-base">
              This operation cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex flex-row items-center gap-4">
            <Button
              className="w-full text-lg font-bold text-slate-900"
              variant="outline"
              onClick={onClose}
              disabled={isPending}
            >
              No
            </Button>
            <Button
              className="w-full text-lg font-bold text-white"
              variant="default"
              onClick={async () => {
                await handleCancelSession();
                onClose();
              }}
              disabled={isPending}
            >
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Yes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
